/** @jsx jsx */
import { jsx } from '@emotion/core'
import {
  Grid,
  freeEditEngine,
  virtualizedGridRenderer,
} from '@integec/grid-tools'
import { Button, Icon, Select, Tabs, Upload, message } from 'antd'
import {
  getAuthorization,
  getCredentials,
  getSolarContribCondtition,
} from 'api/pgads'
import { ErrorContext, SpinContext } from 'context'
import {
  getApiTargetInfo,
  rowHeaderRender,
  getEnvConfig,
  getPermitActions,
} from 'core'
import {
  JcTmpErrorTable,
  isErrorContains,
} from 'core/component/JcTmpErrorDialog'
import { Content, CriteriaRow, FullHeightSizer, Title } from 'core/screen-util'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { connect } from 'react-redux'
import {
  resubmitSolarEnergyStorageData,
  resubmitSolarEnergyStoragePerformanceData,
  resubmitSolarEventData,
  resubmitSolarInverterGroupData,
  resubmitSolarPerformanceData,
  resubmitSolarPlantData,
} from 'solar/solarapi'
import {
  SolarEnergyStoragePerformanceHeaders,
  SolarInverterGroupHeaders,
  SolarInverterGroupPerformanceHeaders,
  SolarNercEventHeaders,
  SolarPlantHeaders,
} from './solarNercImportHeaders'
import { SolarStorageGroupHeaders } from '../Headers'
import { hasAccesPermits } from 'core/utils'
import * as ScreenPermissions from 'core/EventActions'
import {
  getValidEnergyStorageRetrieve,
  getValidEnergyStorageSubmit,
  getValidInverterGroupRetrieve,
  getValidInverterGroupSubmit,
  getValidPlantRetrieve,
  getValidPlantSubmit,
} from './solarNercImportUtils'
import { getSystemConfigurationInfo } from '../../api/pgads'
import { strCol } from '@integec/grid-tools/lib/cols'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'
import { YNBooleanTypes } from 'core/HeaderColTypeProps'

const PLANT = 'plant'
const EVENT = 'event'
const EVENT_FLAT = 'event_flat'
const INVERTER_GROUP = 'inverter_group'
const INVERTER_GROUP_PERFORMANCE = 'inverter_group_performance'
const ENERGY_STORAGE = 'energy_storage'
const ENERGY_STORAGE_PERFORMANCE = 'energy_storage_performance'
// const EVENT_INVERTER_PERFORMANCE = 'event_inverter_performance'

const SolarNercFileImport = ({ targetInfo, actionPermits, envConfig }) => {
  const { setError } = useContext(ErrorContext)
  const { setSpinning } = useContext(SpinContext)
  const [selectedType, setSelectedType] = useState()
  const [activeKey, setActiveKey] = useState('data')
  const [data, setData] = useState([])
  const [jcTmpErrors, setJcTmpErrors] = useState([])
  const [contribConditionList, setContribConditionList] = useState([])

  const [fileName, setFileName] = useState('')

  const [editInfo, setEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )
  const [isAllowed, setIsAllowed] = useState({ event: false, perf: false })

  const changePermission = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: envConfig.authGodMode,
        actionPermits,
        eventActions: [ScreenPermissions.CHANGE_PERMISSIONS],
        matchAny: true,
      }),
    [actionPermits, envConfig.authGodMode]
  )

  useEffect(
    _ => {
      getSystemConfigurationInfo(targetInfo).fork(setError, data => {
        const event = data.find(
          d => d.key === 'SOLAR_IMPORT_XML_NERC_EVENT'
        ) || { value: 'NO' }
        const perf = data.find(
          d => d.key === 'SOLAR_IMPORT_XML_NERC_PERFORMANCE'
        ) || { value: 'NO' }
        const val = {
          event: event.value === 'YES',
          perf: perf.value === 'YES',
        }
        if (val.event) {
          setSelectedType(EVENT)
        } else if (val.perf) {
          setSelectedType(INVERTER_GROUP_PERFORMANCE)
        } else setSelectedType(INVERTER_GROUP)
        setIsAllowed(val)
      })
    },
    [targetInfo, setError]
  )

  const importApi = useMemo(
    _ => {
      switch (selectedType) {
        case EVENT:
          return `${targetInfo.baseUrl}/solar-file-creation/import/event`
        case EVENT_FLAT:
          return `${targetInfo.baseUrl}/solar-file-creation/import/event-flat`
        case INVERTER_GROUP_PERFORMANCE:
          return `${targetInfo.baseUrl}/solar-file-creation/import/performance`
        case PLANT:
          return `${targetInfo.baseUrl}/solar-file-creation/import/plant`
        case INVERTER_GROUP:
          return `${targetInfo.baseUrl}/solar-file-creation/import/inverter-group`
        case ENERGY_STORAGE:
          return `${targetInfo.baseUrl}/solar-file-creation/import/energy-storage`
        case ENERGY_STORAGE_PERFORMANCE:
          return `${targetInfo.baseUrl}/solar-file-creation/import/energy-storage-performance`
      }
    },
    [selectedType, targetInfo.baseUrl]
  )

  const headers = useCallback(
    width => {
      let finalHeaders = []
      switch (selectedType) {
        case EVENT:
        case EVENT_FLAT:
          finalHeaders = SolarNercEventHeaders(width, contribConditionList)
          break
        case INVERTER_GROUP_PERFORMANCE:
          finalHeaders = SolarInverterGroupPerformanceHeaders
          break
        case PLANT:
          finalHeaders = SolarPlantHeaders
          break
        case INVERTER_GROUP:
          finalHeaders = SolarInverterGroupHeaders
          break
        case ENERGY_STORAGE: {
          finalHeaders = SolarStorageGroupHeaders.filter(
            d =>
              !['transferToEntityId', 'modifier', 'modifiedDate'].includes(
                d.ident
              )
          )
          break
        }
        case ENERGY_STORAGE_PERFORMANCE:
          finalHeaders = SolarEnergyStoragePerformanceHeaders
          break
      }
      return [
        strCol({
          ident: 'included',
          display: 'Included',
          ...toSelectionColProps(YNBooleanTypes),
        }),
        ...finalHeaders,
      ]
    },
    [contribConditionList, selectedType]
  )

  const fileProps = {
    name: 'file',
    action: importApi,
    method: 'POST',
    data: {},
    headers: getAuthorization(targetInfo),
    ...getCredentials(targetInfo),
  }

  useEffect(
    _ => {
      getSolarContribCondtition(targetInfo).fork(
        _ => _,
        setContribConditionList
      )
    },
    [targetInfo]
  )

  const setResponse = (mdata, warError) => {
    setSpinning(false)
    setJcTmpErrors(warError)
    if (warError.length > 0) {
      setActiveKey('errors')
    }
    if (!isErrorContains(warError)) {
      message.success('File imported successfully')
      setData([])
    } else {
      if (selectedType === PLANT) {
        mdata = getValidPlantRetrieve(mdata)
      } else if (selectedType === INVERTER_GROUP) {
        mdata = getValidInverterGroupRetrieve(mdata)
        //  mdata = mdata
      } else if (selectedType === ENERGY_STORAGE) {
        mdata = getValidEnergyStorageRetrieve(mdata)
      }
      const finalData = mdata.map(d => ({ ...d, included: 'Y' }))
      setData(finalData)
    }

    setEditInfo(freeEditEngine.generateInitialEditInfo())
  }

  const onFileUpload = data => {
    setFileName(data.file.name)

    if (data.file.status === 'error' || data.file.status === 'removed') {
      setError(data.file.response)
      setSpinning(false)
    } else if (data.file.status === 'done') {
      setSpinning(false)

      const warError = data.file.response.errors
      // setJcTmpErrors(warError)
      const mdata = data.file.response.data
      setResponse(mdata, warError)
    }
  }

  const onResubmitData = _ => {
    const saveData = freeEditEngine.applyEdits({
      data: data.filter(d => d.updated),
      editInfo,
    })

    if (saveData.length <= 0) {
      message.error('Please, update data')
      return
    }

    setSpinning(true)

    if (selectedType === EVENT || selectedType === EVENT_FLAT) {
      const finalData = data.filter(d => d.included === 'Y')
      if (finalData.length <= 0) {
        message.error('Please, select data to resubmit')
        setSpinning(false)
      } else {
        resubmitSolarEventData(targetInfo)(finalData).fork(
          setError,
          response => {
            const { data, errors } = response
            setResponse(data, errors)
          }
        )
      }
    } else if (selectedType === INVERTER_GROUP_PERFORMANCE) {
      const finalData = data.filter(d => d.included === 'Y')
      if (finalData.length <= 0) {
        message.error('Please, select data to resubmit')
        setSpinning(false)
      } else {
        resubmitSolarPerformanceData(targetInfo)(finalData).fork(
          setError,
          response => {
            const { data, errors } = response
            setResponse(data, errors)
          }
        )
      }
    } else if (selectedType === PLANT) {
      const plantData = getValidPlantSubmit(data)
      const finalData = plantData.filter(d => d.included === 'Y')
      if (finalData.length <= 0) {
        message.error('Please, select data to resubmit')
        setSpinning(false)
      } else {
        resubmitSolarPlantData(targetInfo)(finalData).fork(
          setError,
          response => {
            const { data, errors } = response
            setResponse(getValidPlantRetrieve(data), errors)
          }
        )
      }
    } else if (selectedType === INVERTER_GROUP) {
      const invGroupData = getValidInverterGroupSubmit(data)
      const finalData = invGroupData.filter(d => d.included === 'Y')
      if (finalData.length <= 0) {
        message.error('Please, select data to resubmit')
        setSpinning(false)
      } else {
        resubmitSolarInverterGroupData(targetInfo)(finalData).fork(
          setError,
          response => {
            const { data, errors } = response
            setResponse(getValidInverterGroupRetrieve(data), errors)
          }
        )
      }
    } else if (selectedType === ENERGY_STORAGE) {
      const invGroupData = getValidEnergyStorageSubmit(data)
      const finalData = invGroupData.filter(d => d.included === 'Y')
      if (finalData.length <= 0) {
        message.error('Please, select data to resubmit')
        setSpinning(false)
      } else {
        resubmitSolarEnergyStorageData(targetInfo)(finalData).fork(
          setError,
          response => {
            const { data, errors } = response
            setResponse(getValidEnergyStorageRetrieve(data), errors)
          }
        )
      }
    } else if (selectedType === ENERGY_STORAGE_PERFORMANCE) {
      const finalData = data.filter(d => d.included === 'Y')
      if (finalData.length <= 0) {
        message.error('Please, select data to resubmit')
        setSpinning(false)
      } else {
        resubmitSolarEnergyStoragePerformanceData(targetInfo)(finalData).fork(
          setError,
          response => {
            const { data, errors } = response
            setResponse(data, errors)
          }
        )
      }
    }
    setSpinning(false)
  }

  const editRow = editInfo => {
    const modData = data.map(d => {
      if (editInfo.updatedMap.get(d)) {
        return {
          ...editInfo.updatedMap.get(d),
          // modificationInfo: { user: null, modDate: moment() },
          modificationInfo: undefined,
          updated: true,
        }
      } else {
        return d
      }
    })

    let editData = editInfo.updated
    editData = editData.map(d => ({
      ...d,
      // modificationInfo: { user: null, modDate: moment() },
      modificationInfo: undefined,
    }))
    setData(modData)
    setEditInfo({ ...editInfo, updated: editData })
    setData(modData)
  }
  const GridRef = useRef()

  // const isEditable = ({ header }) => {
  //   if (header.ident === 'modifier' || header.ident === 'modifiedDate')
  //     return false

  //   return !header.isKey || header.isEditable || false
  // }
  const isEditable = true

  return (
    <div>
      <CriteriaRow
        css={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '250px 300px 150px auto 150px auto',
          gap: 15,
        }}
      >
        <Title>Solar NERC File Import</Title>
        <Select
          value={selectedType}
          onChange={plantId => {
            setSelectedType(plantId)
            setData([])
            setJcTmpErrors([])
          }}
          style={{ width: '100%' }}
        >
          {isAllowed.event && (
            <Select.Option key={EVENT} value={EVENT}>
              Events
            </Select.Option>
          )}
          {isAllowed.event && (
            <Select.Option key={EVENT_FLAT} value={EVENT_FLAT}>
              Events (Generated from NERC-Excel)
            </Select.Option>
          )}
          {isAllowed.perf && (
            <Select.Option
              key={INVERTER_GROUP_PERFORMANCE}
              value={INVERTER_GROUP_PERFORMANCE}
            >
              Inverter Group Performance
            </Select.Option>
          )}

          <Select.Option
            key={ENERGY_STORAGE_PERFORMANCE}
            value={ENERGY_STORAGE_PERFORMANCE}
          >
            Storage Performance
          </Select.Option>
          {changePermission && (
            <Select.Option key={PLANT} value={PLANT}>
              Plant
            </Select.Option>
          )}
          {changePermission && (
            <Select.Option key={INVERTER_GROUP} value={INVERTER_GROUP}>
              Inverter Group
            </Select.Option>
          )}
          {changePermission && (
            <Select.Option key={ENERGY_STORAGE} value={ENERGY_STORAGE}>
              Storage
            </Select.Option>
          )}
        </Select>
        <Upload
          {...fileProps}
          beforeUpload={_ => setSpinning(true)}
          onChange={data => {
            setSpinning(true)
            setData([])
            setError([])
            onFileUpload(data)
          }}
          multiple={false}
          showUploadList={false}
        >
          <Button type="primary">
            <Icon type="upload" /> Import xml
          </Button>
        </Upload>
        {/* <div style={{ boxSizing: 'border-box', fontSize: '18px' }}> */}
        {/* <span style={{ color: 'grey', fontWeight: 'bolder' }}>File: - </span> */}
        <div>
          Last File Imported : <b>{fileName}</b>
        </div>
        <Button
          onClick={() => {
            setSpinning(true)
            onResubmitData()
          }}
          disabled={editInfo.updated.length <= 0}
          icon="edit"
          type="danger"
        >
          Re Submit
        </Button>
        <div />
      </CriteriaRow>
      <FullHeightSizer heightOffset={200} widthOffset={20}>
        {({ height, width }) => (
          <Content style={{ padding: '10px', height, width }}>
            <Tabs activeKey={activeKey} onChange={setActiveKey} type="card">
              <Tabs.TabPane key="data" tab="Data">
                <div style={{ width: width, height: height }}>
                  <Grid
                    ref={GridRef}
                    data={data}
                    // data={tempData}
                    headers={headers(width) || []}
                    // headers={tempHeaders}
                    altBy={u => u.eventId}
                    altBgColor="#ebebeb"
                    onEditInfoChange={info => editRow(info)}
                    editInfo={editInfo}
                    // onSelectionChange={rows => setRowsSelected(rows.selectedRows)}
                    render={virtualizedGridRenderer({
                      autoFixColByKey: false,
                      rowHeaderRender,
                      height: height - 80,
                      width: width - 30,
                      headerRowHeight: 50,
                    })}
                    //   fuzzyFilter={fuzzyFilter}
                    isEditable={isEditable}
                    // onSelectionChange={({ selectedRows }) => {
                    //   setEventSelected(selectedRows)
                    // }}
                    editMode="cell"
                    selectionMode="single"
                    selectionType="cell"
                  />
                </div>
              </Tabs.TabPane>
              <Tabs.TabPane key="errors" tab="Warnings/Errors">
                <div style={{ width: width }}>
                  <JcTmpErrorTable
                    errors={jcTmpErrors}
                    scroll={{ y: height - 120 }}
                  />
                </div>
              </Tabs.TabPane>
            </Tabs>
          </Content>
        )}
      </FullHeightSizer>
    </div>
  )
}

const mapStateToProps = state => ({
  targetInfo: getApiTargetInfo(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
  // units: getUnits(state),
})

export default connect(mapStateToProps)(SolarNercFileImport)

// export default SolarNercFileImport
